import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader } from "@/components/common/loader";
import { createSubmission } from "@/features/efiling/api";
import {
  CHANNELS,
  type IRSSubmissionDetail,
  type NewSubmissionItem,
  type SubmissionChannel,
  type SubmissionItemKind,
} from "@/features/efiling/types";
import { listClientDocuments } from "@/features/documents/api";
import type { ClientDocument } from "@/features/documents/types";
import { listClientFormSubmissions } from "@/features/forms/api";
import type { FormSubmission } from "@/features/forms/types";
import { ApiError } from "@/lib/api";

interface Props {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (submission: IRSSubmissionDetail) => void;
}

/** Default item kind for a channel, so the practitioner doesn't set it per item. */
function kindForChannel(channel: SubmissionChannel): SubmissionItemKind {
  if (channel === "MEF") return "RETURN";
  if (channel === "SUBMIT_FORMS_ONLINE" || channel === "TAX_PRO_ACCOUNT") return "POA";
  if (channel === "DOC_UPLOAD_TOOL") return "NOTICE_RESPONSE";
  return "ATTACHMENT";
}

export function NewSubmissionDialog({ clientId, open, onOpenChange, onCreated }: Props) {
  const [channel, setChannel] = useState<SubmissionChannel>("SUBMIT_FORMS_ONLINE");
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [forms, setForms] = useState<FormSubmission[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [selectedForms, setSelectedForms] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChannel("SUBMIT_FORMS_ONLINE");
    setSelectedDocs(new Set());
    setSelectedForms(new Set());
    setLoading(true);
    Promise.all([
      listClientDocuments(clientId),
      listClientFormSubmissions(clientId),
    ])
      .then(([docs, subs]) => {
        setDocuments(docs);
        // Only completed forms can be filed.
        setForms(subs.filter((s) => s.status === "COMPLETED"));
      })
      .catch(() => toast.error("Couldn't load this client's forms and documents."))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  const selectedCount = selectedDocs.size + selectedForms.size;
  const channelHelp = useMemo(
    () => CHANNELS.find((c) => c.value === channel)?.help ?? "",
    [channel],
  );

  function toggle(set: Set<string>, id: string, setter: (s: Set<string>) => void) {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  }

  async function handleCreate() {
    if (selectedCount === 0) {
      toast.error("Select at least one form or document.");
      return;
    }
    const kind = kindForChannel(channel);
    const items: NewSubmissionItem[] = [
      ...[...selectedForms].map((id) => ({ form_submission: id, kind })),
      ...[...selectedDocs].map((id) => ({ document: id, kind })),
    ];
    setSubmitting(true);
    try {
      const submission = await createSubmission(clientId, { channel, items });
      toast.success("Draft submission created.");
      onCreated(submission);
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't create the submission.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New IRS submission</DialogTitle>
          <DialogDescription>
            Choose how you'll file and pick the forms or documents to include. The
            filing is attributed to this client's assigned practitioner; the actual
            transmission runs after you review and transmit.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="channel">Filing channel</Label>
            <Select value={channel} onValueChange={(v) => setChannel(v as SubmissionChannel)}>
              <SelectTrigger id="channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANNELS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">{channelHelp}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <PickList
                title="Completed forms"
                empty="No completed forms for this client yet."
                items={forms.map((f) => ({
                  id: f.id,
                  primary: `Form ${f.formNumber}`,
                  secondary: f.formTitle,
                }))}
                selected={selectedForms}
                onToggle={(id) => toggle(selectedForms, id, setSelectedForms)}
              />
              <PickList
                title="Documents"
                empty="No documents for this client yet."
                items={documents.map((d) => ({
                  id: d.id,
                  primary: d.name,
                  secondary: d.formNumber ? `Form ${d.formNumber}` : d.statusDisplay,
                }))}
                selected={selectedDocs}
                onToggle={(id) => toggle(selectedDocs, id, setSelectedDocs)}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={submitting || selectedCount === 0}>
            {submitting ? "Creating…" : `Create draft (${selectedCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PickList({
  title,
  empty,
  items,
  selected,
  onToggle,
}: {
  title: string;
  empty: string;
  items: { id: string; primary: string; secondary?: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-foreground text-sm font-semibold">{title}</p>
      {items.length === 0 ? (
        <p className="text-muted-foreground text-xs">{empty}</p>
      ) : (
        <div className="border-border max-h-40 overflow-y-auto rounded-md border">
          {items.map((item) => (
            <label
              key={item.id}
              className="hover:bg-muted flex cursor-pointer items-center gap-3 px-3 py-2"
            >
              <Checkbox
                checked={selected.has(item.id)}
                onCheckedChange={() => onToggle(item.id)}
                aria-label={`Select ${item.primary}`}
              />
              <span className="min-w-0">
                <span className="text-foreground block truncate text-sm">{item.primary}</span>
                {item.secondary && (
                  <span className="text-muted-foreground block truncate text-xs">
                    {item.secondary}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
